import React, { useRef, useEffect, useState, useCallback } from 'react';
import { MessageSquare, ChevronDown, AlertCircle, RotateCcw } from 'lucide-react';
import { MessageItem } from './MessageItem';
import { DateHeader } from './DateHeader';
import { AITypingIndicator } from './AITypingIndicator';
import { sortMessagesByDate } from '@/lib/message-utils';
import { interleaveResetMarkers, isResetMarkerStreamItem, type ConversationResetMarker } from '@/lib/conversation-reset-markers';
import type { Message } from '@/types/message-types';
import { Button } from '@/components/ui/button';
import { format } from 'date-fns';
import { useToast } from '@/hooks/use-toast';
import { useTranslation } from 'react-i18next';
import { WhatsAppErrorPanel } from './WhatsAppErrorPanel';
import { Card, CardContent } from '@/components/ui/card';
import { MediaDebugPanel } from '@/components/debug/MediaDebugPanel';
import { ScrollArea } from '@/components/ui/scroll-area';
import { aggregateReactions } from '@/utils/reactionUtils';

interface MessageListProps {
  messages: Message[];
  isLoading: boolean;
  customerName?: string;
  conversationId?: string;
  tenantId?: string;
  onCopyMessage: (content: string) => void;
  isManualRefresh?: boolean;
  whatsappError?: unknown;
  dataError?: string;
  showTemplateSelector?: boolean;
  onShowTemplateSelector?: (show: boolean) => void;
  onClearError?: () => void;
  onTemplateSelect?: () => void;
  aiMode?: 'auto' | 'suggestions' | 'manual';
  isAIProcessing?: boolean;
  loadMoreMessages?: () => Promise<void>;
  hasMoreMessages?: boolean;
  isLoadingMore?: boolean;
  /** "Conversation reset" markers to interleave into the thread — see
   * src/lib/conversation-reset-markers.ts and src/hooks/useConversationResetMarkers.ts. Sourced
   * from conversation_events, never from `messages`, so this is purely additive to the stream. */
  resetMarkers?: ConversationResetMarker[];
}

export function MessageList({
  messages,
  isLoading,
  customerName = 'Customer',
  conversationId,
  tenantId,
  onCopyMessage,
  isManualRefresh = false,
  whatsappError,
  dataError,
  showTemplateSelector,
  onShowTemplateSelector,
  onClearError,
  onTemplateSelect,
  aiMode = 'manual',
  isAIProcessing = false,
  loadMoreMessages,
  hasMoreMessages = false,
  isLoadingMore = false,
  resetMarkers = []
}: MessageListProps) {
  const { t } = useTranslation('conversations');
  const toastHook = useToast();
  const toast = toastHook.toast;
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const loadMoreObserverRef = useRef<HTMLDivElement>(null);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [currentVisibleDate, setCurrentVisibleDate] = useState<string | null>(null);
  const [previousScrollHeight, setPreviousScrollHeight] = useState(0);
  const [highlightedMessageId, setHighlightedMessageId] = useState<string | null>('');
  
  // Sort messages by creation date (newest first for WhatsApp-like experience)
  const sortedMessages = sortMessagesByDate(messages);

  // Aggregate reactions and filter out standalone reaction messages
  const { messagesWithReactions, reactionMessageIds } = aggregateReactions(sortedMessages);

  // Create single message stream with interspersed date headers, session dividers, and
  // "Conversation reset" markers (placed by timestamp — see interleaveResetMarkers).
  const createMessageStream = useCallback(() => {
    const stream: Array<
      | { type: 'date'; date: string }
      | { type: 'session'; date: string; conversationId: string }
      | { type: 'reset'; id: string; resetAt: string }
      | { type: 'message'; messageData: Record<string, unknown> }
    > = [];
    let lastDate = '';
    let lastConversationId = '';

    const merged = interleaveResetMarkers(
      messagesWithReactions,
      resetMarkers,
      (item) => item.message.created_at,
    );

    merged.forEach((entry) => {
      if (isResetMarkerStreamItem(entry)) {
        stream.push({ type: 'reset', id: entry.id, resetAt: entry.resetAt });
        return;
      }

      const { message, reactions } = entry;
      const messageDate = format(new Date(message.created_at), 'yyyy-MM-dd');

      // Session divider: when conversation_id changes between adjacent messages
      if (lastConversationId && message.conversation_id && message.conversation_id !== lastConversationId) {
        stream.push({ type: 'session', date: messageDate, conversationId: message.conversation_id });
      }

      if (messageDate !== lastDate) {
        stream.push({ type: 'date', date: messageDate });
        lastDate = messageDate;
      }

      stream.push({ type: 'message', messageData: { message, reactions } });
      if (message.conversation_id) {
        lastConversationId = message.conversation_id;
      }
    });

    return stream;
  }, [messagesWithReactions, resetMarkers]);

  // Advanced scroll to bottom with proper timing and debugging
  const scrollToBottom = useCallback((force: boolean = false) => {
    const scrollContainer = scrollAreaRef.current?.querySelector('[data-radix-scroll-area-viewport]') as HTMLElement;
    if (!scrollContainer) {
      console.log('🔽 No scroll container found');
      return;
    }

    console.log('🔽 Scroll attempt:', { 
      force, 
      scrollHeight: scrollContainer.scrollHeight, 
      scrollTop: scrollContainer.scrollTop,
      clientHeight: scrollContainer.clientHeight,
      messageCount: messages.length
    });
    
    // For forced scrolls (new messages), always scroll without checks
    if (force) {
      console.log('🔽 Force scrolling - bypassing proximity check');
      
      // Use multiple requestAnimationFrame to ensure DOM is fully updated
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (scrollContainer) {
            const targetScrollTop = scrollContainer.scrollHeight - scrollContainer.clientHeight;
            scrollContainer.scrollTop = Math.max(0, targetScrollTop);
            
            console.log('🔽 Force scroll completed:', { 
              targetScrollTop,
              actualScrollTop: scrollContainer.scrollTop,
              scrollHeight: scrollContainer.scrollHeight 
            });
            
            // Verify scroll worked - if not, try again after small delay
            setTimeout(() => {
              if (scrollContainer.scrollTop < targetScrollTop - 10) {
                console.log('🔽 Scroll verification failed, retrying...');
                scrollContainer.scrollTop = Math.max(0, targetScrollTop);
              }
            }, 100);
          }
        });
      });
      return;
    }
    
    // For manual scrolls, check proximity
    const threshold = 100;
    const isNearBottom = scrollContainer.scrollHeight - scrollContainer.scrollTop - scrollContainer.clientHeight < threshold;
    
    console.log('🔽 Proximity check:', { 
      isNearBottom, 
      threshold,
      distanceFromBottom: scrollContainer.scrollHeight - scrollContainer.scrollTop - scrollContainer.clientHeight
    });
    
    if (isNearBottom) {
      const targetScrollTop = scrollContainer.scrollHeight - scrollContainer.clientHeight;
      scrollContainer.scrollTop = Math.max(0, targetScrollTop);
      console.log('🔽 Manual scroll completed');
    } else {
      console.log('🔽 Not near bottom, skipping scroll');
    }
  }, [messages.length]);

  // V1300 Phase 2: Scroll to specific message by WhatsApp message ID and highlight it
  const scrollToMessage = useCallback((whatsappMessageId: string) => {
    console.log('🔍 scrollToMessage called:', { whatsappMessageId });

    const scrollContainer = scrollAreaRef.current?.querySelector('[data-radix-scroll-area-viewport]') as HTMLElement;
    if (!scrollContainer) {
      console.log('❌ No scroll container found');
      return;
    }

    console.log('✅ Scroll container found, searching for message element');

    // Find message element by external_id
    const messageElement = scrollContainer.querySelector(`[data-whatsapp-message-id="${whatsappMessageId}"]`) as HTMLElement;
    if (!messageElement) {
      console.log('❌ Message not found:', whatsappMessageId);
      // List all whatsapp message IDs to debug
      const allMessages = scrollContainer.querySelectorAll('[data-whatsapp-message-id]');
      console.log('Available message IDs:', Array.from(allMessages).map(el => el.getAttribute('data-whatsapp-message-id')));
      return;
    }

    console.log('✅ Message element found:', messageElement);

    // Scroll to message
    messageElement.scrollIntoView({ behavior: 'smooth', block: 'center' });

    // Add highlight animation via React state (not DOM manipulation)
    setHighlightedMessageId(whatsappMessageId);

    // Clear highlight after 4 seconds (2 repetitions × 2s each)
    setTimeout(() => {
      setHighlightedMessageId('');
    }, 4000);
  }, []);

  // Handler for manual scroll button click
  const handleScrollButtonClick = useCallback(() => {
    console.log('Manual scroll button clicked');
    scrollToBottom(true); // Force scroll for manual button click
  }, [scrollToBottom]);

  // Check scroll position for scroll-to-bottom button (to newest messages)
  const checkScrollPosition = useCallback(() => {
    // Use the same viewport element as scrollToBottom
    const scrollContainer = scrollAreaRef.current?.querySelector('[data-radix-scroll-area-viewport]') as HTMLElement;
    if (scrollContainer) {
      const threshold = 100;
      const isAtBottom = scrollContainer.scrollTop >= scrollContainer.scrollHeight - scrollContainer.clientHeight - threshold;
      setShowScrollButton(!isAtBottom && sortedMessages.length > 0);
      console.log('Scroll position check:', { 
        isAtBottom, 
        scrollTop: scrollContainer.scrollTop, 
        scrollHeight: scrollContainer.scrollHeight, 
        clientHeight: scrollContainer.clientHeight 
      });
    }
  }, [sortedMessages.length]);

  // Handle image copying
  const handleCopyImage = useCallback(async (imageUrl: string, messageId: string) => {
    try {
      if (navigator.clipboard && window.ClipboardItem) {
        try {
          const img = new Image();
          img.crossOrigin = 'anonymous';
          
          await new Promise((resolve, reject) => {
            img.onload = resolve;
            img.onerror = reject;
            img.src = imageUrl;
          });

          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          canvas.width = img.width;
          canvas.height = img.height;
          ctx?.drawImage(img, 0, 0);

          canvas.toBlob(async (blob) => {
            if (blob) {
              const item = new ClipboardItem({ [blob.type]: blob });
              await navigator.clipboard.write([item]);

              toast({
                title: t('media.imageCopied'),
                description: t('media.imageCopiedDesc'),
              });
            }
          }, 'image/png');
          
          return;
        } catch (corsError) {
          console.log('CORS error, trying URL copy:', corsError);
        }
      }
      
      await navigator.clipboard.writeText(imageUrl);
      toast({
        title: t('media.imageUrlCopied'),
        description: t('media.imageUrlCopiedDesc'),
      });

    } catch (error) {
      console.error('Copy failed:', error);
      toast({
        title: t('media.copyFailed'),
        description: t('media.copyFailedDesc'),
        variant: "destructive",
      });
    }
  }, [toast, t]);

  // Effect for automatic load more detection
  useEffect(() => {
    const loadMoreElement = loadMoreObserverRef.current;
    if (!loadMoreElement || !loadMoreMessages || !hasMoreMessages || isLoadingMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry.isIntersecting) {
          loadMoreMessages();
        }
      },
      { threshold: 0.1 }
    );

    observer.observe(loadMoreElement);
    return () => observer.disconnect();
  }, [loadMoreMessages, hasMoreMessages, isLoadingMore]);

  // Fix 21d: Use Radix viewport element for scroll measurements (not the wrapper)
  // Effect for maintaining scroll position when loading more messages
  useEffect(() => {
    const viewport = scrollAreaRef.current?.querySelector('[data-radix-scroll-area-viewport]') as HTMLElement;
    if (!viewport || isLoadingMore) return;

    const handleScrollPositionAfterLoad = () => {
      if (previousScrollHeight > 0) {
        const newScrollHeight = viewport.scrollHeight;
        const heightDifference = newScrollHeight - previousScrollHeight;
        if (heightDifference > 0) {
          viewport.scrollTop = viewport.scrollTop + heightDifference;
        }
        setPreviousScrollHeight(0);
      }
    };

    // Small delay to ensure DOM has updated
    const timeoutId = setTimeout(handleScrollPositionAfterLoad, 50);
    return () => clearTimeout(timeoutId);
  }, [messages.length, previousScrollHeight]);

  // Effect for saving scroll position before loading more
  useEffect(() => {
    if (isLoadingMore) {
      const viewport = scrollAreaRef.current?.querySelector('[data-radix-scroll-area-viewport]') as HTMLElement;
      if (viewport) {
        setPreviousScrollHeight(viewport.scrollHeight);
      }
    }
  }, [isLoadingMore]);

  // Scroll event listener
  useEffect(() => {
    // Use the same viewport element as scrollToBottom
    const scrollContainer = scrollAreaRef.current?.querySelector('[data-radix-scroll-area-viewport]') as HTMLElement;
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', checkScrollPosition);
      return () => scrollContainer.removeEventListener('scroll', checkScrollPosition);
    }
  }, [checkScrollPosition]);

  // Track conversation changes and loading state for auto-scroll
  const conversationIdRef = useRef(conversationId);
  const isLoadingAfterConversationChange = useRef(false);
  const isInitialMount = useRef(true);
  
  // Mark when we change conversations (messages will be cleared)
  useEffect(() => {
    if (conversationIdRef.current !== conversationId) {
      conversationIdRef.current = conversationId;
      isLoadingAfterConversationChange.current = true;
      setHighlightedMessageId('');  // Clear highlight when conversation changes (empty string prevents null===null match)
    }
  }, [conversationId]);
  
  // Auto-scroll when new messages arrive with debouncing
  // Fix 21c: Only force-scroll on initial load / conversation change; respect reading position for realtime messages
  const scrollTimeoutRef = useRef<NodeJS.Timeout>();
  useEffect(() => {
    if (messages.length > 0) {
      // Clear any existing timeout
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }

      // Schedule scroll after a small delay to allow DOM updates
      scrollTimeoutRef.current = setTimeout(() => {
        if (isLoadingAfterConversationChange.current || isInitialMount.current) {
          // First load for this conversation — always scroll to bottom
          scrollToBottom(true);
          isLoadingAfterConversationChange.current = false;
          isInitialMount.current = false;
        } else {
          // Subsequent messages (realtime) — only scroll if user is near bottom
          const viewport = scrollAreaRef.current?.querySelector('[data-radix-scroll-area-viewport]') as HTMLElement;
          if (viewport) {
            const distanceFromBottom = viewport.scrollHeight - viewport.scrollTop - viewport.clientHeight;
            if (distanceFromBottom < 150) {
              scrollToBottom(false); // smooth scroll, not forced
            }
          }
        }
      }, 50);
    }

    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, [messages.length, scrollToBottom, isLoading, conversationId]);

  if (isLoading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-sm text-muted-foreground">{t('loadingMessages')}</p>
        </div>
      </div>
    );
  }

  if (dataError && sortedMessages.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 text-destructive mx-auto mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-2">{t('errorLoadingMessages')}</h3>
          <p className="text-sm text-muted-foreground mb-4">
            {t('errorLoadingMessagesDesc')}
          </p>
        </div>
      </div>
    );
  }

  // A reset marker with zero messages still needs to render (that's the reported case: reset,
  // then nothing since) — only fall back to the empty state when there's truly nothing to show.
  if (sortedMessages.length === 0 && resetMarkers.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center">
          <MessageSquare className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-2">{t('noMessages')}</h3>
          <p className="text-sm text-muted-foreground">
            {t('startConversation')}
          </p>
        </div>
      </div>
    );
  }

  const messageStream = createMessageStream();

  return (
    <div className="relative h-full">
      <ScrollArea 
        ref={scrollAreaRef} 
        className="h-full whatsapp-chat-container"
      >
        {/* Error Panels - stay at top outside justify-end */}
        <WhatsAppErrorPanel
          error={whatsappError}
          conversationId={conversationId}
          customerPhone={conversationId}
          showTemplateSelector={showTemplateSelector}
          onShowTemplateSelector={onShowTemplateSelector}
          onClearError={onClearError}
          onTemplateSelect={onTemplateSelect}
        />

        {dataError && (
          <div className="mb-4 mx-2">
            <Card>
              <CardContent className="p-4">
                <div className="text-sm text-destructive">
                  Error loading messages: {dataError}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Message stream in normal order */}
        <div className="flex flex-col min-h-full py-2 w-full">
          {/* Load more observer target - positioned at the top */}
          {hasMoreMessages && (
            <div 
              ref={loadMoreObserverRef}
              className="h-1"
            />
          )}
          
          {/* Loading indicator for older messages */}
          {isLoadingMore && (
            <div className="flex justify-center py-2">
              <div className="flex items-center gap-2 text-muted-foreground text-sm">
                <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                Loading older messages...
              </div>
            </div>
          )}
          
          {messageStream.map((item, index) =>
            item.type === 'reset' ? (
              <div key={`reset-${item.id}`} className="flex items-center gap-3 my-3 px-4">
                <div className="flex-1 border-t border-dashed border-muted-foreground/30 dark:border-muted-foreground/20" />
                <span className="flex items-center gap-1.5 text-xs text-muted-foreground/70 dark:text-muted-foreground/50 whitespace-nowrap">
                  <RotateCcw className="h-3 w-3" />
                  {t('resetConversation.marker', { time: format(new Date(item.resetAt), 'HH:mm') })}
                </span>
                <div className="flex-1 border-t border-dashed border-muted-foreground/30 dark:border-muted-foreground/20" />
              </div>
            ) : item.type === 'session' ? (
              <div key={`session-${item.conversationId}`} className="flex items-center gap-3 my-3 px-4">
                <div className="flex-1 border-t border-dashed border-muted-foreground/30 dark:border-muted-foreground/20" />
                <span className="text-xs text-muted-foreground/60 dark:text-muted-foreground/40 whitespace-nowrap">
                  {t('sessionDivider', { defaultValue: 'New conversation' })}
                </span>
                <div className="flex-1 border-t border-dashed border-muted-foreground/30 dark:border-muted-foreground/20" />
              </div>
            ) : item.type === 'date' ? (
              <DateHeader key={`date-${item.date}`} date={item.date} />
            ) : item.type === 'message' ? (
              <MessageItem
                key={item.messageData.message.id}
                message={item.messageData.message}
                reactions={item.messageData.reactions}
                customerName={customerName}
                tenantId={tenantId}
                onCopyMessage={onCopyMessage}
                onCopyImage={handleCopyImage}
                onScrollToMessage={scrollToMessage}
                isHighlighted={item.messageData.message.external_id === highlightedMessageId}
              />
            ) : null
          )}
          
          {/* AI Typing Indicator - shown when AI is processing in auto or suggestions mode */}
          {isAIProcessing && (aiMode === 'auto' || aiMode === 'suggestions') && (
            <AITypingIndicator
              mode={aiMode}
              className="px-4"
              isOutgoing={true}
            />
          )}

          {/* Scroll to bottom button (newest messages) -- N-14: `sticky` inside the
              scroll viewport instead of `absolute` against the outer wrapper, so it can
              only ever sit within the visible message area. An `absolute` button anchored
              to the wrapper's own box drifted onto the composer on phone widths whenever
              that wrapper's measured height did not match the visible viewport. */}
          {showScrollButton && (
            <div className="sticky bottom-2 z-10 flex justify-end pr-2 pointer-events-none">
              <Button
                size="sm"
                className="rounded-full shadow-lg bg-primary hover:bg-primary/90 pointer-events-auto"
                onClick={handleScrollButtonClick}
              >
                <ChevronDown className="h-4 w-4" />
              </Button>
            </div>
          )}
        </div>
      </ScrollArea>

      <MediaDebugPanel />
    </div>
  );
}